/* eslint-disable react/jsx-no-target-blank */
import { useIntl } from "react-intl";
import { AsideMenuItemWithSub } from "./AsideMenuItemWithSub";
import { AsideMenuItem } from "./AsideMenuItem";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { getUserCustomerAccess, hasPermission, PERMISSIONS } from "../../../helpers/UserRoleHelpers";
import axios from "axios";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { checkIsActive, KTSVG } from "../../../helpers";
import { useLayout } from "../../core";

// Types for menu structure
interface MenuItemConfig {
  to: string;
  titleId: string;
  defaultTitle: string;
  hasBullet?: boolean;
  requiredPermission?: string;
  isBeta?: boolean;
}

interface MenuSectionConfig {
  to: string;
  titleId: string;
  defaultTitle: string;
  icon?: string;
  fontIcon?: string;
  requiredPermission?: string;
  items: MenuItemConfig[];
}

export function AsideMenuMain() {
  const intl = useIntl();
  const { config } = useLayout();
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState<string>("");
  const [hasRetrospective, setHasRetrospective] = useState(false);
  const [slackOpsCount, setSlackOpsCount] = useState<number>(0);

  const { user } = useAuth0();

  useEffect(() => {
    if (user) {
      const access = getUserCustomerAccess(user);
      setIsAdmin(access.isKonfidencyAdmin);
      
      // Get current customer from localStorage or default
      const storageCustomer = window.localStorage.getItem("activeCustomer");
      const customer = storageCustomer || access.primaryCustomer || access.customers[0] || "";
      setCurrentCustomer(customer);
    }
  }, [user]);

  useEffect(() => {
    if (currentCustomer) {
      // Verificar se retrospectiva está disponível para este cliente
      axios.get(`/retrospective/2025/available`)
        .then(response => {
          setHasRetrospective(response.data.available);
        })
        .catch(() => {
          setHasRetrospective(false);
        });
    }
  }, [currentCustomer]);

  // Buscar contagem de assuntos do Slack Ops (apenas para admins)
  useEffect(() => {
    if (!isAdmin) return;

    const fetchSlackOpsCount = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/slack/ops-tracker`);
        const data = response.data;
        
        // Calcular total de assuntos em aberto (open + in_progress + attention)
        const totalOpen = 
          (data.summary.open?.total || 0) +
          (data.summary.in_progress?.total || 0) +
          (data.summary.attention?.total || 0);
        
        setSlackOpsCount(totalOpen);
      } catch (error) {
        console.error('Erro ao buscar contagem do Slack Ops:', error);
        // Em caso de erro, não atualiza (mantém valor anterior)
      }
    };

    // Buscar imediatamente
    fetchSlackOpsCount();

    // Atualizar a cada 1 minuto
    const interval = setInterval(fetchSlackOpsCount, 60000);

    return () => clearInterval(interval);
  }, [isAdmin]);

  // Helper function to check permissions
  const canAccess = (permission?: string) => {
    if (!permission) return true; // No permission required
    if (!user || !currentCustomer) return false;
    return hasPermission(user, currentCustomer, permission);
  };

  // Declarative menu configuration
  const menuSections: MenuSectionConfig[] = [
    {
      to: "/reviews",
      titleId: "aside.submenu.product-reviews",
      defaultTitle: "Avaliações de produtos",
      fontIcon: "bi-archive",
      icon: "/media/icons/duotone/General/Star.svg",
      items: [
        {
          to: "/reviews/moderate",
          titleId: "aside.submenu.item.moderation",
          defaultTitle: "Moderação",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MODERATE_REVIEWS
        },
        {
          to: "/reviews/all",
          titleId: "aside.submenu.item.all-reviews",
          defaultTitle: "Todas avaliações",
          hasBullet: true
        },
        {
          to: "/reviews/removed",
          titleId: "aside.submenu.item.rejected-reviews",
          defaultTitle: "Avaliações reprovadas",
          hasBullet: true
        },
        {
          to: "/reviews/merchant-center",
          titleId: "aside.submenu.item.google-merchant-center",
          defaultTitle: "Google Merchant Center",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/reviews/exports",
          titleId: "aside.submenu.item.exported-reports",
          defaultTitle: "Relatórios exportados",
          hasBullet: true,
          requiredPermission: PERMISSIONS.EXPORT_REVIEWS
        },
        {
          to: "/reviews/top-rated",
          titleId: "aside.submenu.item.top-rated",
          defaultTitle: "Top Rated",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        }
      ]
    },
    {
      to: "/store-reviews",
      titleId: "aside.submenu.store-reviews",
      defaultTitle: "Avaliações de loja",
      fontIcon: "bi-archive",
      icon: "/media/icons/duotone/Home/Building.svg",
      items: [
        {
          to: "/store-reviews/moderate",
          titleId: "aside.submenu.item.moderation",
          defaultTitle: "Moderação",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MODERATE_STORE_REVIEWS
        },
        {
          to: "/store-reviews/all",
          titleId: "aside.submenu.item.all-reviews",
          defaultTitle: "Todas avaliações",
          hasBullet: true
        },
        {
          to: "/store-reviews/removed",
          titleId: "aside.submenu.item.rejected-reviews",
          defaultTitle: "Avaliações reprovadas",
          hasBullet: true
        },
        {
          to: "/store-reviews/exports",
          titleId: "aside.submenu.item.exported-reports",
          defaultTitle: "Relatórios exportados",
          hasBullet: true,
          requiredPermission: PERMISSIONS.EXPORT_STORE_REVIEWS
        }
      ]
    },
    {
      to: "/questions",
      titleId: "aside.submenu.faq",
      defaultTitle: "Perguntas & Respostas",
      fontIcon: "bi-archive",
      icon: "/media/icons/duotone/Code/Question-circle.svg",
      items: [
        {
          to: "/questions/unanswered",
          titleId: "aside.submenu.item.unanswered",
          defaultTitle: "Não respondidas",
          hasBullet: true,
          requiredPermission: PERMISSIONS.VIEW_QUESTIONS
        },
        {
          to: "/questions/all",
          titleId: "aside.submenu.item.all-questions",
          defaultTitle: "Todas as perguntas",
          hasBullet: true
        }
      ]
    },
    {
      to: "/products",
      titleId: "aside.submenu.products",
      defaultTitle: "Produtos",
      fontIcon: "bi-archive",
      icon: "/media/icons/duotone/Shopping/Barcode.svg",
      items: [
        {
          to: "/products/all",
          titleId: "aside.submenu.item.all-products",
          defaultTitle: "Todas os produtos",
          hasBullet: true
        },
        {
          to: "/products/groups",
          titleId: "aside.submenu.item.product-grouping",
          defaultTitle: "Agrupamento de produtos",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/products/kits",
          titleId: "aside.submenu.item.product-kits",
          defaultTitle: "Kits de produtos",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/products/report",
          titleId: "aside.submenu.item.report",
          defaultTitle: "Relatório",
          hasBullet: true
        },
        {
          to: "/products/sellers",
          titleId: "aside.submenu.item.sellers",
          defaultTitle: "Sellers",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/products/categories",
          titleId: "aside.submenu.item.categories",
          defaultTitle: "Categorias",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        }
      ]
    },
    // Notifications section (Moderators and above)
    {
      to: "/notifications",
      titleId: "aside.submenu.notifications",
      defaultTitle: "Clientes",
      fontIcon: "bi-bell-fill",
      icon: "/media/icons/duotone/Communication/Chat5.svg",
      requiredPermission: PERMISSIONS.MODERATE_REVIEWS,
      items: [
        {
          to: "/notifications/sequences",
          titleId: "aside.submenu.item.interval-management",
          defaultTitle: "Gerenciar cadência",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/notifications/trigger",
          titleId: "aside.submenu.item.send-manually",
          defaultTitle: "Enviar manualmente",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MODERATE_REVIEWS
        },
        {
          to: "/notifications/templates",
          titleId: "aside.submenu.item.email-templates",
          defaultTitle: "Templates de e-mail",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/notifications/bulk-send",
          titleId: "aside.submenu.item.send-bulk",
          defaultTitle: "Enviar base histórica",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/notifications/whatsapp-settings",
          titleId: "aside.submenu.item.whatsapp-settings",
          defaultTitle: "Definições WhatsAp",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/notifications/opt-out",
          titleId: "aside.submenu.item.opt-out",
          defaultTitle: "Opt-out",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_NOTIFICATIONS
        }
        // {
        //   to: "/notifications/whatsapp-account",
        //   titleId: "aside.submenu.item.whatsapp-account",
        //   defaultTitle: "Conta e Configurações WhatsApp",
        //   hasBullet: true,
        //   requiredPermission: PERMISSIONS.VIEW_WHATSAPP
        // },
        // {
        //   to: "/notifications/whatsapp-templates",
        //   titleId: "aside.submenu.item.whatsapp-templates",
        //   defaultTitle: "Templates WhatsApp",
        //   hasBullet: true,
        //   requiredPermission: PERMISSIONS.VIEW_WHATSAPP
        // }
      ]
    },
    // Reports section (Moderators and above)
    {
      to: "/reports",
      titleId: "aside.submenu.reports",
      defaultTitle: "Relatórios",
      fontIcon: "bi-bell-fill",
      icon: "/media/icons/duotone/Interface/Line-03-Up.svg",
      requiredPermission: PERMISSIONS.MODERATE_REVIEWS,
      items: [
        {
          to: "/reports/productivity-report",
          titleId: "aside.submenu.item.productivity",
          defaultTitle: "Produtividade",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_SETTINGS
        },
        {
          to: "/reports/notifications",
          titleId: "aside.submenu.item.notifications-report",
          defaultTitle: "Notificações enviadas",
          hasBullet: true,
          requiredPermission: PERMISSIONS.VIEW_NOTIFICATIONS_REPORTS
        },
        {
          to: "/reports/integrated-orders",
          titleId: "aside.submenu.item.integrated-orders-report",
          defaultTitle: "Pedidos Integrados",
          hasBullet: true,
          requiredPermission: PERMISSIONS.VIEW_ORDERS_REPORTS
        },
      ]
    },
    // Billing section (Admins only)
    {
      to: "/billing",
      titleId: "aside.submenu.payment",
      defaultTitle: "Pagamento",
      fontIcon: "bi-credit-card",
      icon: "/media/icons/duotone/Shopping/Credit-card.svg",
      requiredPermission: PERMISSIONS.MANAGE_SETTINGS,
      items: [
        {
          to: "/billing/invoices",
          titleId: "aside.submenu.item.invoices",
          defaultTitle: "Cobranças",
          hasBullet: true
        }
      ]
    },
    // Settings section (Admins only)
    {
      to: "/crafted/accounts",
      titleId: "aside.submenu.settings",
      defaultTitle: "Clientes",
      fontIcon: "bi-person",
      icon: "/media/icons/duotone/Shopping/Settings.svg",
      requiredPermission: PERMISSIONS.MANAGE_SETTINGS,
      items: [
        {
          to: "/review-settings/store",
          titleId: "aside.submenu.item.store-settings",
          defaultTitle: "Configurações da loja",
          hasBullet: true
        },
        {
          to: "/review-settings/collection-interface",
          titleId: "aside.submenu.item.collection-interface",
          defaultTitle: "Interface de coleta",
          hasBullet: true
        },
        {
          to: "/review-settings/attributes",
          titleId: "aside.submenu.item.review-attributes",
          defaultTitle: "Atributos de avaliações (Produtos)",
          hasBullet: true
        },
        {
          to: "/store-reviews/settings/attributes",
          titleId: "aside.submenu.item.submenu.store-attributes",
          defaultTitle: "Atributos de avaliações (Loja)",
          hasBullet: true
        },
        {
          to: "/review-settings/topics",
          titleId: "aside.submenu.item.submenu.products",
          defaultTitle: "Tópicos de avaliações (Produtos)",
          hasBullet: true
        },
        {
          to: "/store-reviews/settings/topics",
          titleId: "aside.submenu.item.submenu.store-topics",
          defaultTitle: "Tópicos de avaliações (Loja)",
          hasBullet: true
        },
        {
          to: "/review-settings/user-limits",
          titleId: "aside.submenu.item.store-user-limits",
          defaultTitle: "Limites por usuáro",
          hasBullet: true
        },
        {
          to: "/review-settings/rules",
          titleId: "aside.submenu.item.forbidden-words",
          defaultTitle: "Palavras proibidas",
          hasBullet: true
        },
        {
          to: "/review-settings/coupons",
          titleId: "aside.submenu.item.discount-voucher",
          defaultTitle: "Cupons de desconto",
          hasBullet: true
        },
        {
          to: "/notifications/store-cadence",
          titleId: "aside.submenu.item.notifications",
          defaultTitle: "Notificações",
          hasBullet: true
        },
        {
          to: "/review-settings/linktree",
          titleId: "aside.submenu.item.linktree",
          defaultTitle: "Linktree",
          hasBullet: true
        },
        {
          to: "/review-settings/integrations",
          titleId: "aside.submenu.item.integrations",
          defaultTitle: "Integrações",
          hasBullet: true
        },
        {
          to: "/users",
          titleId: "aside.submenu.item.submenu.users",
          defaultTitle: "Usuários",
          hasBullet: true,
          requiredPermission: PERMISSIONS.MANAGE_USERS
        },
        {
          to: "/platform-migration",
          titleId: "aside.submenu.item.platform-migration",
          defaultTitle: "Migração de Plataforma",
          hasBullet: true,
          requiredPermission: PERMISSIONS.PLATFORM_MIGRATE,
          isBeta: true
        }
      ]
    }
  ];

  // NPS section configuration
  const npsSection: MenuSectionConfig = {
    to: "/nps",
    titleId: "aside.submenu.nps",
    defaultTitle: "NPS",
    fontIcon: "bi-archive",
    icon: "/media/icons/duotone/General/Smile.svg",
    items: [
      {
        to: "/nps/overview",
        titleId: "aside.submenu.item.overview",
        defaultTitle: "Visão geral",
        hasBullet: true
      },
      {
        to: "/nps/responses",
        titleId: "aside.submenu.item.responses",
        defaultTitle: "Respostas",
        hasBullet: true
      },
      {
        to: "/nps/settings",
        titleId: "aside.submenu.item.survey-configuration",
        defaultTitle: "Configurar pesquisa",
        hasBullet: true,
        requiredPermission: PERMISSIONS.MANAGE_SETTINGS
      }
    ]
  };

  // Admin sections configuration (Konfidency Admin only)
  const adminSections: MenuSectionConfig[] = [
    {
      to: "/customers",
      titleId: "aside.submenu.customer",
      defaultTitle: "Clientes",
      fontIcon: "bi-archive",
      icon: "/media/icons/duotone/General/Smile.svg",
      items: [
        {
          to: "/customers/list",
          titleId: "aside.submenu.item.customer-management",
          defaultTitle: "Gerenciar clientes",
          hasBullet: true
        },
        {
          to: "/customers/new",
          titleId: "aside.submenu.item.new-customer",
          defaultTitle: "Novo cliente",
          hasBullet: true
        },
        {
          to: "/customers/import-review",
          titleId: "aside.submenu.item.import-review",
          defaultTitle: "Importar avaliações",
          hasBullet: true
        },
        {
          to: "/customers/import-questions",
          titleId: "aside.submenu.item.import-questions",
          defaultTitle: "Importar perguntas",
          hasBullet: true
        },
        {
          to: "/customers/integration-diagnostics",
          titleId: "aside.submenu.item.integration-diagnostics",
          defaultTitle: "Diagnóstico de integração",
          hasBullet: true
        },
        {
          to: "/customers/trustvox-import",
          titleId: "aside.submenu.item.trustvox-import",
          defaultTitle: "Importação Trustvox",
          hasBullet: true
        },
        {
          to: "/migrate-reviews",
          titleId: "aside.submenu.migrate-reviews",
          defaultTitle: "Migrar avaliações",  
          hasBullet: true
        }

      ]
    },
    {
      to: "/customer-success",
      titleId: "aside.submenu.customer-success",
      defaultTitle: "Customer Success",
      fontIcon: "bi-heart-pulse",
      icon: "/media/icons/duotone/General/Heart.svg",
      items: [
        {
          to: "/platform-usage",
          titleId: "aside.submenu.item.platform-usage",
          defaultTitle: "Uso front-end",
          hasBullet: true
        }
      ]
    }
  ];

  // Render menu item with permission check
  const renderMenuItem = (item: MenuItemConfig) => {
    if (!canAccess(item.requiredPermission)) return null;
    
    return (
      <AsideMenuItem
        key={item.to}
        to={item.to}
        title={intl.formatMessage({
          id: item.titleId,
          defaultMessage: item.defaultTitle,
        })}
        hasBullet={item.hasBullet}
        isBeta={item.isBeta}
      />
    );
  };

  // Render menu section with items
  const renderMenuSection = (section: MenuSectionConfig) => {
    // Check if section itself requires permission
    if (section.requiredPermission && !canAccess(section.requiredPermission)) {
      return null;
    }

    const visibleItems = section.items.filter(item => canAccess(item.requiredPermission));
    
    // Don't render section if no items are visible
    if (visibleItems.length === 0) return null;
    
    return (
      <AsideMenuItemWithSub
        key={section.to}
        to={section.to}
        title={intl.formatMessage({
          id: section.titleId,
          defaultMessage: section.defaultTitle,
        })}
        fontIcon={section.fontIcon}
        icon={section.icon}
      >
        {section.items.map(renderMenuItem)}
      </AsideMenuItemWithSub>
    );
  };

  // Render section header
  const renderSectionHeader = (titleId: string, defaultTitle: string) => (
    <div className="menu-item">
      <div className="menu-content pt-8 pb-2">
        <span className="menu-section text-muted text-uppercase fs-8 ls-1">
          {intl.formatMessage({
            id: titleId,
            defaultMessage: defaultTitle,
          })}
        </span>
      </div>
    </div>
  );

  return (
    <>
      {/* Dashboard - Always visible */}
      <AsideMenuItem
        to="/dashboard"
        icon="/media/icons/duotone/Design/PenAndRuller.svg"
        title={intl.formatMessage({
          id: "menu.dashboard",
          defaultMessage: "Dashboard",
        })}
        fontIcon="bi-app-indicator"
      />

      {/* Retrospectiva 2025 - Exibido apenas se houver dados processados */}
      {hasRetrospective && (
        <AsideMenuItem
          to="/retrospective"
          icon="/media/icons/duotone/General/Star.svg"
          title={intl.formatMessage({
            id: "menu.retrospective",
            defaultMessage: "Retrospectiva 2025",
          })}
          fontIcon="bi-star-fill"
        />
      )}

      {/* Konfidency Reviews Section */}
      {renderSectionHeader("aside.menu.reviews.section.title", "Konfidency Reviews")}
      {menuSections.map(renderMenuSection)}

      {/* Konfidency Quest Section */}
      {renderSectionHeader("aside.menu.quest.section.title", "Konfidency Quest")}
      {renderMenuSection(npsSection)}

      {/* Admin Section - Konfidency Admin only */}
      {isAdmin && (
        <>
          {renderSectionHeader("aside.menu.admin.section.title", "Asgard")}
          {adminSections.map(renderMenuSection)}
          <div className='menu-item'>
            <Link 
              className={clsx('menu-link without-sub', {
                active: checkIsActive(window.location.pathname, '/slack-ops-tracker')
              })} 
              to="/slack-ops-tracker"
            >
              {config.aside.menuIcon === 'svg' && (
                <span className='menu-icon'>
                  <KTSVG path="/media/icons/duotone/Communication/Group-chat.svg" className='svg-icon-2' />
                </span>
              )}
              {config.aside.menuIcon === 'font' && <i className={clsx('bi fs-3 bi-slack')}></i>}
              <span className='menu-title'>
                {intl.formatMessage({
                  id: "aside.submenu.item.slack-ops-tracker",
                  defaultMessage: "Rastreador Slack Ops",
                })}
              </span>
              {slackOpsCount > 0 && (
                <span className="badge badge-circle badge-warning ms-2">{slackOpsCount}</span>
              )}
            </Link>
          </div>
          <AsideMenuItem
            to="/cache-manager/clear-cache"
            title={intl.formatMessage({
              id: "aside.submenu.clean-cache",
              defaultMessage: "Limpar cache",
            })}
            hasBullet={false}
            fontIcon="bi-memory"
            icon="/media/icons/duotone/General/Thunder.svg"
          />
          <AsideMenuItem
            to="/auto-injection"
            title={intl.formatMessage({
              id: "aside.submenu.auto-injection",
              defaultMessage: "Auto-injeção (site)",
            })}
            hasBullet={false}
            fontIcon="bi-code"
            icon="/media/icons/duotone/Design/Component.svg"
          />
        </>
      )}
    </>
  );
}